import * as THREE from 'three';

/**
 * The A and B markers painted on the ground at each bomb site.
 *
 * Without these the sites are invisible — you are told to plant at A or B and
 * given nothing in the world to find. Everything here is generated: the letter
 * is drawn to a canvas, the boundary is a line loop, so no binary assets.
 *
 * Drawn as ground decals rather than floating icons so they read as painted on
 * the map (which is how CS marks its sites) and never occlude a target.
 */

const UP = new THREE.Vector3(0, 1, 0);
const DOWN = new THREE.Vector3(0, -1, 0);

/** A big stencilled letter on transparent ground, as a texture. */
function letterTexture(label, px = 256) {
  const c = document.createElement('canvas');
  c.width = c.height = px;
  const g = c.getContext('2d');

  g.clearRect(0, 0, px, px);
  g.translate(px / 2, px / 2);

  // Dark halo under everything. These maps are bright sand and concrete, and a
  // plain white stencil disappears into them — the first version was unreadable
  // in a capture from 7 m away.
  g.strokeStyle = 'rgba(0,0,0,0.55)';
  g.lineWidth = px * 0.085;
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.arc(0, 0, px * 0.40, i * Math.PI / 2 + 0.14, (i + 1) * Math.PI / 2 - 0.14);
    g.stroke();
  }

  // Stencil ring, broken at the diagonals like real floor markings.
  g.strokeStyle = 'rgba(255,246,232,0.98)';
  g.lineWidth = px * 0.045;
  for (let i = 0; i < 4; i++) {
    g.beginPath();
    g.arc(0, 0, px * 0.40, i * Math.PI / 2 + 0.16, (i + 1) * Math.PI / 2 - 0.16);
    g.stroke();
  }

  g.font = `700 ${px * 0.58}px "Arial Black", Impact, sans-serif`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.lineJoin = 'round';
  g.strokeStyle = 'rgba(0,0,0,0.6)';
  g.lineWidth = px * 0.075;
  g.strokeText(label, 0, px * 0.02);
  g.fillStyle = 'rgba(255,246,232,0.98)';
  g.fillText(label, 0, px * 0.02);

  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 8;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

/**
 * Build markers for every site on a level.
 *
 * @param {object} level  needs `bombSites` and a `decor` group
 * @param {object} physics  used to find the actual floor under each site
 * @returns {THREE.Group} added to `level.decor`
 */
export function buildSiteMarkers(level, physics) {
  const group = new THREE.Group();
  group.name = 'siteMarkers';

  for (const site of level.bombSites ?? []) {
    const size = site.box.getSize(new THREE.Vector3());
    // Sit on the real floor, not the box centre — the site volume is tall and
    // its middle is usually a metre or two above the ground.
    const from = site.center.clone();
    from.y = site.box.max.y + 1;
    const hit = physics?.raycast(from, DOWN, size.y + 6);
    const y = (hit ? hit.point.y : site.box.min.y) + 0.03;

    const span = Math.max(4.0, Math.min(size.x, size.z) * 0.8);
    const tex = letterTexture(site.label);
    const decal = new THREE.Mesh(
      new THREE.PlaneGeometry(span, span),
      new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        // Emitted values are scene-referred linear here (OutputPass tone-maps
        // last), so keep this below 1 or the paint blooms like a light.
        color: new THREE.Color(0.82, 0.86, 0.92),
      }),
    );
    decal.rotation.x = -Math.PI / 2;
    decal.position.set(site.center.x, y, site.center.z);
    decal.renderOrder = 2;
    group.add(decal);

    // Boundary of the plantable area, so it is obvious where the bomb can go.
    const hx = Math.min(size.x, 14) / 2;
    const hz = Math.min(size.z, 14) / 2;
    const pts = [
      new THREE.Vector3(-hx, 0, -hz), new THREE.Vector3(hx, 0, -hz),
      new THREE.Vector3(hx, 0, hz), new THREE.Vector3(-hx, 0, hz),
      new THREE.Vector3(-hx, 0, -hz),
    ];
    const line = new THREE.Line(
      new THREE.BufferGeometry().setFromPoints(pts),
      new THREE.LineBasicMaterial({
        color: new THREE.Color(0.7, 0.52, 0.16),
        transparent: true,
        opacity: 0.7,
        depthWrite: false,
      }),
    );
    line.position.set(site.center.x, y + 0.01, site.center.z);
    line.renderOrder = 2;
    group.add(line);

    site.markerY = y;
  }

  (level.decor ?? level.root)?.add(group);
  return group;
}

export { UP };
